'use client';
import React, { useState } from 'react';
import { CheckCircle, Clock, AlertCircle, Globe, Award, ChevronDown, ChevronUp } from 'lucide-react';

interface WBStep {
  id: number;
  step: string;
  description: string;
  lraNexusFeature: string;
  status: 'implemented' | 'partial' | 'planned';
  complianceScore: number;
  module: string;
  alignedCycleStages: number[];
}

const wbSteps: WBStep[] = [
  {
    id: 1,
    step: 'Cadastral Survey & Parcel Identification',
    description: 'Systematic identification and boundary delineation of all land parcels using geospatial methods.',
    lraNexusFeature: 'AI Discovery Engine + Drone Mapping Integration — auto-detects unregistered parcels via satellite imagery and drone surveys across all 15 counties.',
    status: 'implemented',
    complianceScore: 92,
    module: 'GIS Hub → AI Discovery + Drone',
    alignedCycleStages: [1, 2],
  },
  {
    id: 2,
    step: 'Parcel Registration & Geo-Referencing',
    description: 'Assigning unique identifiers and GPS coordinates to each parcel with photo documentation.',
    lraNexusFeature: 'QR Registry with GPS geo-tagging, photo capture, unique property ID (LR-XXX-XXXX format), and satellite map overlay.',
    status: 'implemented',
    complianceScore: 96,
    module: 'GIS Hub → QR Registry',
    alignedCycleStages: [2],
  },
  {
    id: 3,
    step: 'Rights & Ownership Documentation',
    description: 'Recording legal ownership, tenure type, encumbrances, and chain of title for each parcel.',
    lraNexusFeature: 'Ownership Chain Panel — full chain-of-title visualization, document upload, dispute flagging, and taxpayer profile linkage.',
    status: 'implemented',
    complianceScore: 88,
    module: 'GIS Hub → Ownership',
    alignedCycleStages: [3],
  },
  {
    id: 4,
    step: 'Mass Valuation & Classification',
    description: 'Computer-assisted mass appraisal (CAMA) using property attributes, location, and market data.',
    lraNexusFeature: 'Property Valuation Panel — CAMA-style valuation engine with category classification (residential, commercial, industrial, farmland, vacant).',
    status: 'implemented',
    complianceScore: 84,
    module: 'GIS Hub → Valuation',
    alignedCycleStages: [4],
  },
  {
    id: 5,
    step: 'Tax Computation & Notice Issuance',
    description: 'Automated calculation of tax liability and generation of official demand notices.',
    lraNexusFeature: 'Tax Bill Generation Panel — automated assessment calculation, PDF demand notice generation, batch issuance with audit trail.',
    status: 'implemented',
    complianceScore: 95,
    module: 'GIS Hub → Tax Bills',
    alignedCycleStages: [5, 6],
  },
  {
    id: 6,
    step: 'Taxpayer Communication & Engagement',
    description: 'Multi-channel notification systems ensuring taxpayers receive and acknowledge their obligations.',
    lraNexusFeature: 'Taxpayer Self-Service Portal — SMS, email, WhatsApp delivery tracking, portal access, payment history, and obligation cards.',
    status: 'implemented',
    complianceScore: 90,
    module: 'Taxpayer Portal',
    alignedCycleStages: [6],
  },
  {
    id: 7,
    step: 'Revenue Collection & Receipt Management',
    description: 'Secure multi-channel payment processing with real-time reconciliation and digital receipts.',
    lraNexusFeature: 'Collections Module — bank, mobile money, and online payment channels with instant digital receipt issuance and treasury reconciliation.',
    status: 'implemented',
    complianceScore: 91,
    module: 'Collections',
    alignedCycleStages: [7],
  },
  {
    id: 8,
    step: 'Compliance Monitoring & Audit Trail',
    description: 'Real-time tracking of payment compliance, arrears, and automated risk-based audit selection.',
    lraNexusFeature: 'Compliance Engine — AI-powered compliance scoring, delinquency tracker, arrears aging, and automated enforcement trigger rules.',
    status: 'implemented',
    complianceScore: 87,
    module: 'Compliance Engine',
    alignedCycleStages: [8],
  },
  {
    id: 9,
    step: 'Enforcement & Cycle Refresh',
    description: 'Legal enforcement actions, penalties, and annual cycle reset with updated property records.',
    lraNexusFeature: 'Enforcement Action Panel + Field Inspection — enforcement case management, penalty calculation, field visit tracking, and annual record refresh.',
    status: 'partial',
    complianceScore: 78,
    module: 'GIS Hub → Enforcement',
    alignedCycleStages: [9],
  },
  {
    id: 10,
    step: 'Interoperability & Data Sharing',
    description: 'Integration with national registries, land courts, financial institutions, and government databases.',
    lraNexusFeature: 'ASYCUDA integration active. National ID linkage planned. Land Court API and financial institution connectivity in roadmap.',
    status: 'partial',
    complianceScore: 62,
    module: 'Enterprise Admin → Integrations',
    alignedCycleStages: [3, 7],
  },
  {
    id: 11,
    step: 'Capacity Building & Change Management',
    description: 'Training programs, user adoption tracking, and institutional change management.',
    lraNexusFeature: 'County Offices Module — field officer management, training status tracking, and performance monitoring by county.',
    status: 'partial',
    complianceScore: 70,
    module: 'County Offices',
    alignedCycleStages: [],
  },
  {
    id: 12,
    step: 'Monitoring, Evaluation & Reporting',
    description: 'KPI dashboards, impact assessment, and donor/government reporting frameworks.',
    lraNexusFeature: 'Revenue Analytics + Executive Dashboard — real-time KPIs, IMF/World Bank benchmark comparisons, county performance reports, and export capabilities.',
    status: 'implemented',
    complianceScore: 93,
    module: 'Revenue Analytics',
    alignedCycleStages: [8, 9],
  },
];

const statusConfig = {
  implemented: { label: 'Implemented', color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: CheckCircle },
  partial: { label: 'Partial', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: Clock },
  planned: { label: 'Planned', color: '#6b7280', bg: 'rgba(107,114,128,0.12)', icon: AlertCircle },
};

export default function WorldBankGISFrameworkPanel() {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [view, setView] = useState<'matrix' | 'detail'>('matrix');

  const implemented = wbSteps.filter(s => s.status === 'implemented').length;
  const partial = wbSteps.filter(s => s.status === 'partial').length;
  const avgScore = Math.round(wbSteps.reduce((a, b) => a + b.complianceScore, 0) / wbSteps.length);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div
        className="rounded-xl p-5"
        style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.03) 100%)', border: '1px solid rgba(59,130,246,0.2)' }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(59,130,246,0.15)', border: '1.5px solid rgba(59,130,246,0.3)' }}
            >
              <Globe size={20} style={{ color: '#60a5fa' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>
                World Bank GIS Implementation Framework
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                LRA Nexus mapped against World Bank land administration & GIS standards
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(['matrix', 'detail'] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all"
                style={{
                  backgroundColor: view === v ? 'rgba(59,130,246,0.2)' : 'var(--dark-surface)',
                  color: view === v ? '#60a5fa' : 'var(--dark-muted)',
                  border: '1px solid var(--dark-border)',
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Score summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Overall Compliance Score', value: `${avgScore}%`, color: '#60a5fa' },
            { label: 'Fully Implemented', value: `${implemented}/${wbSteps.length}`, color: '#10b981' },
            { label: 'Partially Implemented', value: `${partial}/${wbSteps.length}`, color: '#f59e0b' },
            { label: 'WB Standards Met', value: `${implemented + partial}/${wbSteps.length}`, color: '#a78bfa' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg p-3"
              style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
            >
              <p className="text-xl font-bold font-mono" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance score bar */}
      <div
        className="rounded-xl p-4"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>
            Readiness Matrix — Per Standard
          </h3>
          <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--dark-muted)' }}>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
                {cfg.label}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {wbSteps.map((step) => {
            const cfg = statusConfig[step.status];
            return (
              <div key={step.id} className="flex items-center gap-3">
                <span className="text-xs font-mono w-5 text-right flex-shrink-0" style={{ color: 'var(--dark-muted)' }}>
                  {step.id}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-medium truncate" style={{ color: 'var(--dark-text)' }}>
                      {step.step}
                    </span>
                    <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: cfg.color }}>
                      {step.complianceScore}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${step.complianceScore}%`,
                        backgroundColor: cfg.color,
                        boxShadow: `0 0 6px ${cfg.color}60`,
                      }}
                    />
                  </div>
                </div>
                <span
                  className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                  style={{ backgroundColor: cfg.bg, color: cfg.color }}
                >
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail view */}
      {view === 'detail' && (
        <div className="space-y-3">
          {wbSteps.map((step) => {
            const cfg = statusConfig[step.status];
            const isExpanded = expandedStep === step.id;
            return (
              <div
                key={step.id}
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
              >
                <button
                  className="w-full flex items-center gap-3 p-4 text-left transition-all hover:bg-white/3"
                  onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                >
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}
                  >
                    {step.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                        {step.step}
                      </h4>
                      {step.alignedCycleStages.length > 0 && (
                        <div className="flex gap-1">
                          {step.alignedCycleStages.map(s => (
                            <span
                              key={s}
                              className="text-xs px-1.5 py-0.5 rounded font-bold"
                              style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}
                            >
                              Cycle {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded"
                      style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      {step.complianceScore}%
                    </span>
                    {isExpanded ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                    <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--dark-muted)' }}>
                          World Bank Standard
                        </p>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-text)' }}>
                          {step.description}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--accent)' }}>
                          LRA Nexus Implementation
                        </p>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-text)' }}>
                          {step.lraNexusFeature}
                        </p>
                        <div
                          className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.2)' }}
                        >
                          <Award size={11} />
                          {step.module}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { wbSteps };
